import { Youtube, MessageCircle, Flower2, Orbit, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const ACCENTS = {
  cyan: "text-cyan-300 border-cyan-300/30 bg-cyan-300/10",
  violet: "text-violet-300 border-violet-400/30 bg-violet-400/10",
  gold: "text-amber-300 border-amber-300/30 bg-amber-300/10",
  magenta: "text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-400/10",
};

const CARDS = [
  {
    n: "01",
    icon: Youtube,
    accent: "cyan",
    title: "WATCH SHIFTS_FREQUENCY",
    text: "Explore videos about consciousness, awakening, energy, frequency, ancient wisdom, and reality.",
    cta: "WATCH ON YOUTUBE",
    href: "https://youtube.com/@shifts_frequency?si=hjI8v_aHHtwATRFl",
    testid: "watch",
  },
  {
    n: "02",
    icon: MessageCircle,
    accent: "violet",
    title: "JOIN THE SHIFT",
    text: "Connect with people exploring spirituality, consciousness, personal growth, energy, and transformation.",
    cta: "ENTER DISCORD",
    href: "https://discord.gg/uvMNCcCYkx",
    testid: "join",
  },
  {
    n: "03",
    icon: Flower2,
    accent: "gold",
    title: "INNER WORK",
    text: "Slow down. Reflect. Heal. Meditate. Reconnect with the part of yourself that already knows the way.",
    cta: "BEGIN YOUR JOURNEY",
    target: "#inner-work",
    testid: "inner-work",
  },
  {
    n: "04",
    icon: Orbit,
    accent: "magenta",
    title: "ENERGY & FREQUENCY",
    text: "Explore ideas surrounding energy, vibration, consciousness, frequency, and the relationship between your inner and outer world.",
    cta: "EXPLORE",
    target: "#energy",
    testid: "energy",
  },
];

const onCardMouse = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

export default function Explore() {
  return (
    <section id="explore" data-testid="explore-section" className="relative px-5 py-24 sm:px-8 sm:py-32">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <p className="eyebrow mb-5" data-testid="explore-eyebrow">Begin Here</p>
          <h2
            data-testid="explore-heading"
            className="font-display max-w-3xl text-3xl font-bold uppercase leading-tight tracking-[0.12em] text-slate-100 sm:text-4xl lg:text-5xl"
          >
            Your Shift Starts <span className="text-glow-cyan text-cyan-300">Within</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.n} delay={i * 0.12}>
                <article
                  data-testid={`explore-card-${c.testid}`}
                  onMouseMove={onCardMouse}
                  className="cosmic-card flex h-full flex-col p-7"
                >
                  <div className="flex items-start justify-between">
                    <span className={`flex h-12 w-12 items-center justify-center rounded-full border ${ACCENTS[c.accent]}`}>
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <span className="font-display text-xs tracking-[0.3em] text-slate-600">{c.n}</span>
                  </div>
                  <h3 className="font-display mt-7 text-sm font-bold leading-relaxed tracking-[0.18em] text-slate-100">
                    {c.title}
                  </h3>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">{c.text}</p>
                  {c.href ? (
                    <a
                      data-testid={`explore-card-${c.testid}-btn`}
                      href={c.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-display mt-7 inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] text-cyan-300 transition-colors duration-300 hover:text-cyan-100"
                    >
                      {c.cta}
                      <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                    </a>
                  ) : (
                    <button
                      data-testid={`explore-card-${c.testid}-btn`}
                      onClick={() => scrollToId(c.target)}
                      className="font-display mt-7 inline-flex items-center gap-2 self-start text-[11px] font-bold tracking-[0.22em] text-cyan-300 transition-colors duration-300 hover:text-cyan-100"
                    >
                      {c.cta}
                      <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                    </button>
                  )}
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
